import { toast } from 'sonner'
import { zodResolver } from '@hookform/resolvers/zod'
import { startOAuthOrReport } from '@/api/endpoints/oauth'
import { Hash, Link as LinkIcon, LogIn, Send, Type, Upload } from 'lucide-react'
import { useEffect, type FormEvent } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@/components/ui/Button'
import { DialogClose, DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { useStartUpload, useUploadStatus, type UploadKickoffIn } from '@/api/endpoints/youtube/uploads'
import { cn } from '@/lib/cn'
import styles from './YoutubeUploadModal.module.scss'

const PRIVACY: ReadonlyArray<SegmentedOption<'private' | 'unlisted' | 'public'>> = [
  { value: 'private', label: 'Privado' },
  { value: 'unlisted', label: 'Não listado' },
  { value: 'public', label: 'Público' },
]

const FIELD_LABELS: Record<string, string> = {
  title: 'Título',
  description: 'Descrição',
  tags: 'Tags',
  privacy: 'Privacidade',
  publish_at: 'Publicação agendada',
  video_id: 'Vídeo',
  language: 'Idioma',
  channel: 'Canal',
}

const TIME_PRESETS = ['04:00', '07:00', '13:00', '17:00'] as const

// Zod model — mirrors the backend UploadKickoffIn shape but with the
// friendly validation messages a form needs (empty strings → required).
// We skip ``.default()`` on the optional fields because the resolver infers
// the input type (pre-default) and the handleSubmit handler gets the output
// type; keeping them symmetrical avoids typing drift.
const schema = z.object({
  title: z.string().trim().min(3, 'Mínimo 3 caracteres').max(100, 'Máximo 100 caracteres'),
  description: z.string().trim().max(5000, 'Máximo 5000 caracteres'),
  tagsInput: z.string(),
  privacy: z.enum(['private', 'unlisted', 'public']),
  publish_date: z.string(),
  publish_time: z.string(),
})

type FormValues = z.infer<typeof schema>

export interface YoutubeUploadModalProps {
  open: boolean
  onClose: () => void
  /** The video about to be published. Title/description/tags default from its metadata. */
  videoId: string
  language: string
  channel: string
  defaultTitle?: string
  defaultDescription?: string
  defaultTags?: ReadonlyArray<string>
  onStarted?: (jobKey: string) => void
}

/**
 * Dialog with the YouTube upload form. Handles three states:
 *   1. OAuth not configured — instructs the user to add client_secret.
 *   2. Configured but not authenticated — offers the OAuth link.
 *   3. Authenticated — shows the form proper.
 *
 * Successful submit hands the ``job_key`` back via ``onStarted`` so the
 * caller can wire it up to the jobs feed. The global JobsWatcher fires
 * the completion toast automatically.
 *
 * Sibling: ``features/platforms/tiktok/upload/TiktokUploadModal`` (Phase 5)
 * mirrors this for TikTok profiles. The platform-agnostic dispatcher in
 * ``features/uploads/components/UploadModal`` picks one based on the
 * channel's platform.
 */
export function YoutubeUploadModal({
  open,
  onClose,
  videoId,
  language,
  channel,
  defaultTitle = '',
  defaultDescription = '',
  defaultTags = [],
  onStarted,
}: YoutubeUploadModalProps) {
  const { data: status, isPending: statusPending } = useUploadStatus(channel)
  const startMutation = useStartUpload()

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: defaultTitle,
      description: defaultDescription,
      tagsInput: defaultTags.join(', '),
      privacy: 'private',
      publish_date: '',
      publish_time: '',
    },
  })

  // Reset when the modal reopens so defaults from a different project
  // don't bleed through the cached form state.
  useEffect(() => {
    if (open) {
      form.reset({
        title: defaultTitle,
        description: defaultDescription,
        tagsInput: defaultTags.join(', '),
        privacy: 'private',
        publish_date: '',
        publish_time: '',
      })
      startMutation.reset()
    }
    // `form` and `startMutation` identities are stable; deps only reflect
    // inputs we care about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultTitle, defaultDescription, defaultTags])

  const privacy = form.watch('privacy')
  const publishTime = form.watch('publish_time')
  const authState = status?.auth
  const canSchedule = privacy !== 'public'

  // Wipe the schedule fields whenever the user flips to public — YouTube
  // refuses publishAt for non-private videos, and a stale value would
  // silently get sent on the next submit if we kept it around.
  useEffect(() => {
    if (!canSchedule) {
      form.setValue('publish_date', '')
      form.setValue('publish_time', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSchedule])

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    void form.handleSubmit((values) => {
      const tags = splitTags(values.tagsInput)
      const publishAt =
        canSchedule && values.publish_date && values.publish_time
          ? toIsoWithLocalTz(values.publish_date, values.publish_time)
          : ''
      const payload: UploadKickoffIn = {
        video_id: videoId,
        language,
        channel,
        title: values.title,
        description: values.description,
        tags,
        privacy: values.privacy,
        // YouTube category 27 = "Education". Always send so the typed
        // payload satisfies the schema; backend defaults to the same.
        category: '27',
        // Primeiro upload nunca fura a guarda anti-duplicata (card #56); o
        // reenvio forçado só existe na retomada de um job de upload.
        force: false,
        ...(publishAt ? { publish_at: publishAt } : {}),
      }
      startMutation.mutate(payload, {
        onSuccess: (data) => {
          onStarted?.(data.job_key)
          onClose()
        },
      })
    })(e)
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title="Publicar no YouTube"
        description="Revise o conteúdo antes de enviar. A publicação pode levar alguns minutos."
        footer={
          authState?.is_authenticated ? (
            <>
              <DialogClose asChild>
                <Button variant="secondary" disabled={startMutation.isPending}>
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                type="submit"
                form="upload-form"
                leadingIcon={<Send />}
                disabled={startMutation.isPending}
              >
                {startMutation.isPending ? 'Enviando...' : 'Enviar'}
              </Button>
            </>
          ) : (
            <DialogClose asChild>
              <Button variant="secondary">Fechar</Button>
            </DialogClose>
          )
        }
      >
        {statusPending ? (
          <p className={styles.hint}>Carregando status do canal...</p>
        ) : !authState?.is_configured ? (
          <NotConfiguredCard channel={channel} />
        ) : !authState.is_authenticated ? (
          <NotAuthenticatedCard channel={channel} />
        ) : (
          <form id="upload-form" className={styles.form} onSubmit={onSubmit} noValidate>
            {startMutation.isError ? (
              <FormErrorSummary error={startMutation.error} fieldLabels={FIELD_LABELS} />
            ) : null}

            <div className={styles.field}>
              <label className={styles.label} htmlFor="upload-title">
                <Type aria-hidden /> Título
              </label>
              <Input
                id="upload-title"
                placeholder="Título que aparece no YouTube"
                {...form.register('title')}
              />
              {form.formState.errors.title ? (
                <span className={styles.hint}>{form.formState.errors.title.message}</span>
              ) : null}
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="upload-description">
                <LinkIcon aria-hidden /> Descrição
              </label>
              <textarea
                id="upload-description"
                className={styles.textarea}
                rows={4}
                placeholder="Descrição (opcional)"
                {...form.register('description')}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="upload-tags">
                <Hash aria-hidden /> Tags
              </label>
              <Input
                id="upload-tags"
                placeholder="Separe por vírgulas. Ex: space, shorts, curiosidades"
                {...form.register('tagsInput')}
              />
            </div>

            <div className={styles.field}>
              <span className={styles.label}>Privacidade</span>
              <Segmented<'private' | 'unlisted' | 'public'>
                options={PRIVACY}
                value={privacy}
                onValueChange={(v) => {
                  form.setValue('privacy', v)
                }}
                aria-label="Privacidade"
              />
            </div>

            {canSchedule ? (
              <div className={styles.field}>
                <span className={styles.label}>Publicação agendada</span>
                <div className={styles.scheduleRow}>
                  <Input
                    id="upload-publish-date"
                    type="date"
                    aria-label="Data de publicação"
                    {...form.register('publish_date')}
                  />
                  <div className={styles.timeCol}>
                    <Input
                      id="upload-publish-time"
                      type="time"
                      aria-label="Horário de publicação"
                      {...form.register('publish_time')}
                    />
                    <div className={styles.timePresets} role="group" aria-label="Horários sugeridos">
                      {TIME_PRESETS.map((preset) => {
                        const active = publishTime === preset
                        return (
                          <button
                            key={preset}
                            type="button"
                            className={cn(styles.timePreset, active && styles.timePresetActive)}
                            onClick={() => {
                              form.setValue('publish_time', preset, { shouldDirty: true })
                            }}
                          >
                            {preset}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>
                <span className={styles.hint}>
                  Deixe em branco para publicar imediatamente. O YouTube exige vídeos privados para
                  agendamento.
                </span>
              </div>
            ) : null}
          </form>
        )}
      </DialogContent>
    </DialogRoot>
  )
}

function splitTags(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .slice(0, 30)
}

// Compose an RFC 3339 timestamp anchored in the user's local timezone.
// `<input type="date">` + `<input type="time">` give wall-clock strings
// with no offset; sending those raw made YouTube interpret them as UTC,
// shifting publication by the user's TZ offset (e.g. UTC-3 → 3h early).
function toIsoWithLocalTz(date: string, time: string): string {
  const local = new Date(`${date}T${time}:00`)
  return local.toISOString()
}

function NotConfiguredCard({ channel }: { channel: string }) {
  return (
    <div className={styles.authCard}>
      <span className={styles.authTitle}>
        <LogIn /> OAuth não configurado
      </span>
      <p className={styles.authDescription}>
        O canal <strong>{channel}</strong> ainda não tem um <code>client_secret</code> do YouTube
        registrado. Adicione o arquivo em <code>data/channels/{channel}/client_secret.json</code> e
        reinicie o servidor.
      </p>
    </div>
  )
}

function NotAuthenticatedCard({ channel }: { channel: string }) {
  return (
    <div className={styles.authCard}>
      <span className={styles.authTitle}>
        <LogIn /> Conecte o YouTube
      </span>
      <p className={styles.authDescription}>
        O canal <strong>{channel}</strong> precisa autorizar o Prism a enviar vídeos. Abra a
        autorização do Google e, ao voltar, tente novamente.
      </p>
      <div className={styles.authActions}>
        <Button
          leadingIcon={<Upload />}
          onClick={() => {
            startOAuthOrReport('youtube', channel, (m) => toast.error(m))
          }}
        >
          Autorizar no YouTube
        </Button>
      </div>
    </div>
  )
}
